// Locating a tutor message's written spans in its rendered bubble.
//
// The server stores each written span's UTF-16 offsets into the message text,
// but the bubble shows that text rendered through the shared markdown pipeline
// (src/scripts/tutorMarkdown.js), whose table repair and typographer change
// the characters, so an offset cannot point into the rendered DOM and the text
// cannot be searched for either. Instead:
//
//   1. a start marker (U+2E00) and an end marker (U+2E01) are inserted into a
//      copy of the text at each candidate span's offsets;
//   2. the copy is rendered, and so is the unmarked text; when the marked HTML
//      with its markers removed differs from the plain HTML, the message is
//      shown plain (a marker changed how markdown-it read something);
//   3. after the render, the DOM pass walks the text nodes in document order,
//      checks that every start and end marker survived in alternating order,
//      and wraps every text segment between the k-th start marker and the k-th
//      end marker as a piece of span k, dropping the markers.
//
// The markers are Supplemental Punctuation characters (general category Po)
// because markdown-it decides what an emphasis delimiter or a quote does from
// whether its neighbour is whitespace, punctuation or a word character, and a
// punctuation neighbour leaves those decisions as they were in every inline
// case the design's review rendered. The one shape that needs help is
// intraword emphasis, handled in insertPhraseMarkers.
//
// Spec: docs/superpowers/specs/2026-09-25-audio-playback-speed-and-phrase-replay-design.md,
// section 3, "How a span is located in the bubble" and "How the wrapping is
// applied".

import { learningLanguageCode } from "@/scripts/phraseMatching.js";
import { renderTutorMarkdown } from "@/scripts/tutorMarkdown.js";
import { PLAIN_RENDER_REASONS } from "@/composables/usePlaybackEvents.js";

export const PHRASE_START_MARKER = "\u2E00";
export const PHRASE_END_MARKER = "\u2E01";
const ANY_MARKER_PATTERN = /[\u2E00\u2E01]/;
// Whitespace holding a line break is what markdown-it writes between block
// elements (table cells, list items, paragraphs). A written span never holds
// a newline (the server drops such a span), so this is never phrase text, and
// wrapping it would put an inline element straight inside a table row or a
// list, where it becomes a stray cell or item.
const STRUCTURAL_WHITESPACE_PATTERN = /^\s*[\r\n]\s*$/;
const ALL_MARKERS_PATTERN = /[\u2E00\u2E01]/g;

// The class every wrapped piece carries. Live state, tint and loading are
// added as further classes by TutorMessage.
export const PHRASE_PIECE_CLASS = "phrase";

// The reasons a flagged message is shown plain, reported to analytics. The
// analytics module declares them, so it can refuse any other value.
export { PLAIN_RENDER_REASONS };

// Characters whose runs open and close emphasis inside a word. An underscore
// is left out on purpose: intraword underscores never form emphasis, so a
// marker next to one changes nothing, while moving the marker outside the run
// would turn the underscores into an emphasis they were not.
const INTRAWORD_EMPHASIS_CHARACTERS = new Set(["*", "~"]);
const FLANKING_WORD_CHARACTER = /^[\p{L}\p{M}\p{N}]$/u;

function isHighSurrogate(code) {
  return code >= 0xd800 && code <= 0xdbff;
}

function isLowSurrogate(code) {
  return code >= 0xdc00 && code <= 0xdfff;
}

// Whether a UTF-16 offset falls between the two halves of a surrogate pair,
// where a marker would split one character in two.
function splitsSurrogatePair(text, offset) {
  if (offset <= 0 || offset >= text.length) return false;
  return isHighSurrogate(text.charCodeAt(offset - 1)) && isLowSurrogate(text.charCodeAt(offset));
}

function codePointBefore(text, index) {
  if (index <= 0) return "";
  if (index >= 2 && isLowSurrogate(text.charCodeAt(index - 1))) {
    if (isHighSurrogate(text.charCodeAt(index - 2))) return text.slice(index - 2, index);
  }
  return text[index - 1];
}

function codePointAt(text, index) {
  if (index >= text.length) return "";
  return String.fromCodePoint(text.codePointAt(index));
}

/**
 * The written spans that become phrases: those whose lang is the learning
 * language's base code, whose offsets lie inside the text in increasing,
 * non-overlapping order on whole characters, and whose stored text is a
 * non-empty string (it is the phrase's accessible name). Each entry keeps the
 * span's index in the stored list, which is the phrase's identity everywhere
 * else.
 * @param {*} text the message text the offsets point into
 * @param {*} writtenSpans the message's writtenSpans
 * @param {*} learningLang the dialog's learning language
 * @returns {{spanIndex: number, start: number, end: number, text: string}[]}
 */
export function phraseCandidates(text, writtenSpans, learningLang) {
  if (typeof text !== "string" || !Array.isArray(writtenSpans)) return [];
  const code = learningLanguageCode(learningLang);
  if (!code) return [];
  const candidates = [];
  let previousEnd = 0;
  writtenSpans.forEach((span, spanIndex) => {
    if (!span || typeof span !== "object") return;
    const { lang, start, end } = span;
    if (typeof lang !== "string" || lang.toLowerCase() !== code) return;
    if (typeof span.text !== "string" || !span.text) return;
    if (!Number.isInteger(start) || !Number.isInteger(end)) return;
    if (start < previousEnd || end <= start || end > text.length) return;
    if (splitsSurrogatePair(text, start) || splitsSurrogatePair(text, end)) return;
    candidates.push({ spanIndex, start, end, text: span.text });
    previousEnd = end;
  });
  return candidates;
}

// Where one candidate's markers go. Normally at its offsets, which the server
// already tightened past whitespace and inline delimiters, so a marker sits
// directly between an emphasis delimiter and its word. That is harmless when
// the character outside the delimiter run is whitespace or punctuation, but
// for intraword emphasis (`これは**猫**です`, `es**muy**bueno`) a punctuation
// marker there stops markdown-it opening or closing the run. So when the span
// is enclosed by delimiter runs on both sides and the character just outside a
// run is a word character, that marker goes outside the run instead.
function markerOffsets(text, candidate) {
  const { start, end } = candidate;
  let runStart = start;
  while (runStart > 0 && INTRAWORD_EMPHASIS_CHARACTERS.has(text[runStart - 1])) runStart--;
  let runEnd = end;
  while (runEnd < text.length && INTRAWORD_EMPHASIS_CHARACTERS.has(text[runEnd])) runEnd++;
  let markerStart = start;
  let markerEnd = end;
  if (runStart < start && runEnd > end) {
    if (FLANKING_WORD_CHARACTER.test(codePointBefore(text, runStart))) markerStart = runStart;
    if (FLANKING_WORD_CHARACTER.test(codePointAt(text, runEnd))) markerEnd = runEnd;
  }
  return [markerStart, markerEnd];
}

/**
 * A copy of `text` with a start and an end marker around each candidate.
 * @param {string} text
 * @param {{start: number, end: number}[]} candidates from phraseCandidates
 * @returns {string}
 */
export function insertPhraseMarkers(text, candidates) {
  const insertions = [];
  let previousEnd = 0;
  candidates.forEach((candidate) => {
    let [markerStart, markerEnd] = markerOffsets(text, candidate);
    // A start marker never moves in front of the previous span's end marker.
    markerStart = Math.max(markerStart, previousEnd);
    insertions.push([markerStart, PHRASE_START_MARKER], [markerEnd, PHRASE_END_MARKER]);
    previousEnd = markerEnd;
  });
  // Stable order: at a shared offset the previous span's end marker comes
  // first, because the insertions were listed in reply order.
  let marked = "";
  let cursor = 0;
  insertions.forEach(([offset, marker]) => {
    marked += text.slice(cursor, offset) + marker;
    cursor = offset;
  });
  return marked + text.slice(cursor);
}

/**
 * The HTML a flagged message shows. With candidates, it is the marked render
 * when that render, markers removed, is identical to the plain render, and the
 * plain render otherwise. `fallback` names why a message with candidates was
 * shown plain, or is null.
 * @param {string} text
 * @param {object[]} candidates from phraseCandidates
 * @param {(text: string) => string} [render]
 * @returns {{html: string, marked: boolean, fallback: string|null}}
 */
export function renderPhraseMarkup(text, candidates, render = renderTutorMarkdown) {
  const plain = render(text);
  if (!candidates || !candidates.length) return { html: plain, marked: false, fallback: null };
  // A marker the model wrote itself would be counted as one of ours.
  if (ANY_MARKER_PATTERN.test(text)) {
    return { html: plain, marked: false, fallback: PLAIN_RENDER_REASONS.MARKER_IN_TEXT };
  }
  const marked = render(insertPhraseMarkers(text, candidates));
  if (marked.replace(ALL_MARKERS_PATTERN, "") !== plain) {
    return { html: plain, marked: false, fallback: PLAIN_RENDER_REASONS.RENDER_MISMATCH };
  }
  return { html: marked, marked: true, fallback: null };
}

function collectTextNodes(node, into = []) {
  node.childNodes.forEach((child) => {
    if (child.nodeType === 3) into.push(child);
    else if (child.nodeType === 1) collectTextNodes(child, into);
  });
  return into;
}

function isInsideAnchor(node, root) {
  for (let current = node.parentNode; current && current !== root; current = current.parentNode) {
    if (current.nodeType === 1 && current.tagName === "A") return true;
  }
  return false;
}

// Checks the markers before anything is wrapped: every candidate's start and
// end marker must be present, in document order and properly alternating.
// Returns the spans that have text inside an anchor, or null when the markers
// do not describe `count` spans.
function readMarkerSequence(textNodes, root, count) {
  const blocked = new Set();
  let closed = 0;
  let open = false;
  for (const node of textNodes) {
    const inAnchor = isInsideAnchor(node, root);
    for (const character of node.data) {
      if (character === PHRASE_START_MARKER) {
        if (open) return null;
        open = true;
        if (inAnchor) blocked.add(closed);
      } else if (character === PHRASE_END_MARKER) {
        if (!open) return null;
        if (inAnchor) blocked.add(closed);
        open = false;
        closed++;
      } else if (open && inAnchor) {
        blocked.add(closed);
      }
    }
  }
  if (open || closed !== count) return null;
  return blocked;
}

/**
 * The DOM pass over a marked render: wraps each span's text segments and
 * removes every marker. A span with any text inside a link gets no pieces at
 * all, so a phrase tap can never also navigate, but its markers are still
 * consumed and the spans after it keep their numbers.
 *
 * Returns {ok: true, pieces} where pieces maps each wrapped span's candidate
 * index to its piece elements in document order, or {ok: false, reason} with
 * the DOM untouched when the markers do not describe `count` spans.
 * @param {Element} root the rendered bubble
 * @param {number} count how many candidates were marked
 */
export function wrapPhraseMarkers(root, count) {
  const textNodes = collectTextNodes(root);
  const blocked = readMarkerSequence(textNodes, root, count);
  if (blocked === null) return { ok: false, reason: PLAIN_RENDER_REASONS.MISSING_PAIR };

  const doc = root.ownerDocument;
  const pieces = new Map();
  let spanIndex = -1;
  let open = false;
  const makePiece = (text) => {
    const list = pieces.get(spanIndex) || [];
    const piece = doc.createElement("span");
    piece.className = PHRASE_PIECE_CLASS;
    piece.setAttribute("data-span", String(spanIndex));
    piece.setAttribute("data-piece", String(list.length));
    // A text node inside the element, so nothing from the model is ever
    // interpreted as markup.
    piece.textContent = text;
    list.push(piece);
    pieces.set(spanIndex, list);
    return piece;
  };
  const wraps = (text) =>
    open && !blocked.has(spanIndex) && !STRUCTURAL_WHITESPACE_PATTERN.test(text);

  for (const node of textNodes) {
    const data = node.data;
    if (!ANY_MARKER_PATTERN.test(data)) {
      // A whole text node between the markers, such as the bold word in the
      // middle of `me **gustó** mucho`. An empty one is discarded.
      if (data && wraps(data)) node.parentNode.replaceChild(makePiece(data), node);
      continue;
    }
    const fragment = doc.createDocumentFragment();
    let buffer = "";
    const flush = () => {
      if (!buffer) return;
      fragment.appendChild(wraps(buffer) ? makePiece(buffer) : doc.createTextNode(buffer));
      buffer = "";
    };
    for (const character of data) {
      if (character === PHRASE_START_MARKER) {
        flush();
        spanIndex++;
        open = true;
      } else if (character === PHRASE_END_MARKER) {
        flush();
        open = false;
      } else {
        buffer += character;
      }
    }
    flush();
    node.parentNode.replaceChild(fragment, node);
  }
  return { ok: true, pieces };
}

/**
 * Removes every marker character from the text under `root`. Used when a pass
 * fails part-way, so no marker is ever left on screen while the message
 * re-renders plain.
 * @param {Element} root
 */
export function stripPhraseMarkers(root) {
  collectTextNodes(root).forEach((node) => {
    if (ANY_MARKER_PATTERN.test(node.data)) {
      node.data = node.data.replace(ALL_MARKERS_PATTERN, "");
    }
  });
}
